import type {
  ClaimWorksheetValidationError,
  Ub04FieldLocator,
} from '../../../services/api/types.gen';

/**
 * EMR-857: server-computed worksheet findings, indexed for rendering.
 *
 * The backend keys every finding to a UB-04 form locator (and a row, for the
 * repeated blocks), so the editor can outline the exact input that carries the
 * problem instead of showing one prose string in the dialog footer.
 */

export type WorksheetErrorMap = Map<string, ClaimWorksheetValidationError[]>;

/**
 * The DOM/lookup key for one addressable worksheet input.
 *
 * Doubles as the `data-field-anchor` value, which is what lets `scrollToField`
 * jump from a summary-panel item to the input.
 */
export const errorKey = (
  locator: Ub04FieldLocator | string,
  row?: string | null
): string => (row ? `${locator}::${row}` : String(locator));

export function buildErrorMap(
  errors: readonly ClaimWorksheetValidationError[] | null | undefined
): WorksheetErrorMap {
  const map: WorksheetErrorMap = new Map();
  for (const error of errors ?? []) {
    const key = errorKey(error.locator, error.row);
    const existing = map.get(key);
    if (existing) {
      existing.push(error);
    } else {
      map.set(key, [error]);
    }
  }
  return map;
}

/** Backend finding severity: `error` blocks Create Claim, `warning` does not. */
export type WorksheetFindingSeverity = 'error' | 'warning';

/**
 * Input outline classes for findings. Live here rather than in
 * `WorksheetFieldError` so that component file exports only components
 * (react-refresh/only-export-components).
 */

/** The red outline applied to an input that carries a blocking finding. */
export const worksheetErrorInputClass = 'border-red-500 bg-red-50';

/** The amber outline for an input whose findings are all advisory warnings. */
export const worksheetWarningInputClass = 'border-amber-500 bg-amber-50';

/** The outline class matching a finding's severity. */
export const worksheetFindingInputClass = (
  severity: WorksheetFindingSeverity | undefined
): string =>
  severity === 'warning'
    ? worksheetWarningInputClass
    : worksheetErrorInputClass;

const findingsFor = (
  map: WorksheetErrorMap,
  locator: Ub04FieldLocator | string,
  row?: string | null
): ClaimWorksheetValidationError[] | undefined =>
  map.get(errorKey(locator, row)) ??
  (row ? map.get(errorKey(locator)) : undefined);

/**
 * The message(s) to render under one input, or undefined when it is clean.
 *
 * A row-addressed lookup falls back to the bare locator so a block-level
 * finding (one with no row) still surfaces on every row of that block rather
 * than vanishing.
 */
export function fieldError(
  map: WorksheetErrorMap,
  locator: Ub04FieldLocator | string,
  row?: string | null
): string | undefined {
  const found = findingsFor(map, locator, row);
  if (!found?.length) return undefined;
  return found.map(error => error.message).join(' ');
}

/**
 * The severity to style one input's findings with, or undefined when clean.
 *
 * An input carrying both an error and a warning styles as an error — the
 * blocking problem is the one the biller must not miss.
 */
export function fieldSeverity(
  map: WorksheetErrorMap,
  locator: Ub04FieldLocator | string,
  row?: string | null
): WorksheetFindingSeverity | undefined {
  const found = findingsFor(map, locator, row);
  if (!found?.length) return undefined;
  return found.some(error => error.severity !== 'warning')
    ? 'error'
    : 'warning';
}

/**
 * The advanced-section locators today's backend rules can emit.
 *
 * NOT every locator rendered by `ShowAllUb04Fields` — only the ones a current
 * rule anchors findings to. The editor uses this to auto-expand that section
 * when a finding lands there, and to expand-then-scroll when the summary panel
 * links to one — otherwise the click would target an input that is not in the
 * DOM. When a new backend rule targets another advanced-section locator
 * (FL 9, 11-13, 16, 29, 66-74, ...), add it here or its findings lose
 * auto-expand and the summary link dead-ends.
 *
 * Lives here rather than in `ShowAllUb04Fields` so that component file exports
 * only components (react-refresh/only-export-components).
 */
export const SHOW_ALL_FIELDS_LOCATORS = [
  '01-name',
  '01-phone',
  '05',
  '08-first',
  '08-last',
  '10',
  '56',
] as const;

/** Whether any finding targets one of the given locators. */
export function hasErrorForLocators(
  errors: readonly ClaimWorksheetValidationError[] | null | undefined,
  locators: readonly string[]
): boolean {
  const wanted = new Set(locators);
  return (errors ?? []).some(error => wanted.has(error.locator));
}
